// src/seo/blog/esaHousingGuides.ts
//
// PAWTENANT-ESA-HOUSING-GUIDES-PUBLISH-001
//
// ONE record per ESA housing guide, and the ONLY place each guide's title,
// meta description, hero image and FAQ copy live. Same design as
// src/seo/blog/esaLetterCluster.ts: the visible FAQ accordion, the build-time
// <head> JSON-LD (getRouteHeadJsonLd in src/prerender/entry.tsx), the client
// <BlogArticleSchema>, CORE_PAGE_META in src/config/seoConfig.ts and the
// og:image pass in scripts/prerender-seo.mjs all read these records, so none of
// them can drift from the others. scripts/check-esa-housing-guides.mjs asserts it.
//
// SOURCE CONTENT
// The three owner-approved PDFs of 2026-09-24:
//   * "What Documents Do Landlords Require for an ESA?"
//   * "ESA Housing Approval Process: A Step-by-Step Guide"
//   * "ESA Housing Mistakes to Avoid as a Tenant"
// Titles, meta descriptions and slugs are the PDFs' own. FAQ copy is verbatim;
// the only edits are PDF-extraction repairs (line-wrap joins) and "esa" → "ESA"
// where the source lower-cased the acronym.
//
// PAWTENANT-ESA-HOUSING-GUIDES-HUD-CLEANUP-002: six FAQ answers (HUD complaint,
// no-pets, pet fees x2, training, denial) are qualified against HUD's
// 2025-09-17 withdrawal and 2026-05-22 enforcement memorandum, at the owner's
// request. The rest of the FAQ copy remains the PDFs' own.
//
// Dates are the real publication date. Author and publisher are the PawTenant
// organisation — no individual reviewer or credential is claimed, because none
// was supplied with the source content.

export interface HousingGuideFaq {
  q: string;
  a: string;
}

export interface HousingGuide {
  /** Canonical route path. */
  route: string;
  /** The page's single <h1>. */
  h1: string;
  /** <title> — also the CORE_PAGE_META title. The PDF's "SEO Title". */
  metaTitle: string;
  /** <meta name="description"> — the PDF's "Meta Description". */
  metaDescription: string;
  /** Final BreadcrumbList crumb name, and the card title in listings. */
  breadcrumb: string;
  /** Site-root-relative path to the 1200x630 hero/social image. */
  heroImage: string;
  heroAlt: string;
  heroWidth: number;
  heroHeight: number;
  datePublished: string;
  dateModified: string;
  articleSection: string;
  keywords: string[];
  /** Remix icon used for this guide's card in the related-guides section. */
  icon: string;
  faqs: HousingGuideFaq[];
}

export const BASE_URL = "https://pawtenant.com";

/** Where the guides are listed, and the middle crumb of every guide's breadcrumb. */
export const HOUSING_GUIDES_PARENT = { to: "/resource-center", label: "Resource Center" } as const;

const HERO_W = 1200;
const HERO_H = 630;

export const ESA_HOUSING_GUIDES: Record<string, HousingGuide> = {
  "/what-documents-do-landlords-require-for-esa": {
    route: "/what-documents-do-landlords-require-for-esa",
    h1: "What Documents Do Landlords Require for an Emotional Support Animal?",
    metaTitle: "What Documents Do Landlords Require for an ESA?",
    metaDescription:
      "Learn what documents landlords require for an ESA, what your housing letter should include, and how to request an emotional support animal accommodation.",
    breadcrumb: "What Documents Do Landlords Require for an ESA?",
    heroImage: "/assets/blog/esa-housing-guides/landlord-esa-documents-1200x630.webp",
    heroAlt: "Tenant holding a small dog while filling out housing paperwork at a desk with a laptop",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-24",
    dateModified: "2026-09-24",
    articleSection: "Housing Rights",
    keywords: [
      "what documents do landlords require for an ESA",
      "landlord emotional support animal letter",
      "ESA housing letter",
      "emotional support animal letter requirements",
      "emotional support animal letter for housing",
    ],
    icon: "ri-file-list-3-line",
    faqs: [
      {
        q: "What documents do landlords require for an emotional support animal?",
        a: "Landlords may request reliable disability-related information when your disability and need for the animal are not apparent or already known. An ESA letter from a qualified healthcare professional is a common form of supporting documentation. A written accommodation request and applicable animal health records may also be relevant.",
      },
      {
        q: "Can a landlord ask for proof of an emotional support animal?",
        a: "Yes. A landlord may request reliable information establishing your disability-related need for an emotional support animal when that information is not already apparent or known. However, the requested information should be limited to what is necessary to evaluate your housing accommodation.",
      },
      {
        q: "Does an ESA letter need to include my medical diagnosis?",
        a: "Generally, no. An ESA letter should establish your disability-related need for the animal, but your landlord ordinarily does not need your specific diagnosis, complete medical history, or detailed treatment records.",
      },
      {
        q: "Can I submit an ESA letter obtained online to my landlord?",
        a: "Yes. Documentation obtained through an online evaluation may support a housing accommodation request when it comes from a qualified healthcare professional with sufficient knowledge of your circumstances. However, purchasing a generic ESA certificate without a meaningful evaluation does not, by itself, establish your need for an assistance animal.",
      },
      {
        q: "Does my landlord need the original ESA letter?",
        a: "Federal fair housing law does not establish a universal requirement to submit an original paper ESA letter. A digital copy may be sufficient to support your request. If your landlord has reasonable concerns about the document's authenticity, ask what verification is needed.",
      },
      {
        q: "Can a landlord require annual ESA letter renewal?",
        a: "The Fair Housing Act does not establish a universal rule that every ESA letter expires after one year. However, a landlord may request updated disability-related information when reasonably necessary to evaluate an accommodation request. Applicable state requirements and individual circumstances may also affect documentation needs.",
      },
      {
        q: "Do I need ESA registration documents for an apartment?",
        a: "No. Federal fair housing law does not require you to purchase an ESA registration certificate or identification card. When documentation is needed, reliable information establishing your disability-related need for the animal is more relevant than a commercial registration certificate.",
      },
      {
        q: "What should I do if my landlord refuses to accept my ESA documentation?",
        a: "Ask your landlord to explain the reason for the refusal. If your documentation is incomplete, you may be able to provide relevant clarification. If you believe your accommodation request was unlawfully denied, consider contacting a fair housing organization, your state or local fair housing agency, or an attorney. HUD's current enforcement policy focuses on individually trained assistance animals, so a federal complaint about an untrained emotional support animal may not be pursued; private legal action and state or local protections may still be available.",
      },
    ],
  },

  "/esa-housing-approval-process": {
    route: "/esa-housing-approval-process",
    h1: "ESA Housing Approval Process: Step-by-Step Guide",
    metaTitle: "ESA Housing Approval Process: A Step-by-Step Guide",
    metaDescription:
      "Learn how the ESA housing approval process works, what documents you may need, and how to request a reasonable accommodation from your landlord.",
    breadcrumb: "ESA Housing Approval Process",
    heroImage: "/assets/blog/esa-housing-guides/esa-housing-approval-process-1200x630.webp",
    heroAlt: "Couple kneeling outdoors with their black and white dog in front of a house",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-24",
    dateModified: "2026-09-24",
    articleSection: "Housing Rights",
    keywords: [
      "ESA housing approval process",
      "how to get emotional support animal letter",
      "emotional support animal housing letter",
      "emotional support animal letter housing requirements",
      "ESA housing letter",
    ],
    icon: "ri-route-line",
    faqs: [
      {
        q: "How do I get ESA housing approval?",
        a: "To request ESA housing approval, establish your disability-related need for an emotional support animal, obtain supporting documentation if necessary, and submit a reasonable accommodation request to your landlord. Your housing provider will then evaluate the request based on the applicable requirements and individual circumstances.",
      },
      {
        q: "Do I need an ESA letter before requesting housing approval?",
        a: "Not always. If your disability and disability-related need for the animal are already apparent or known, additional documentation may not be necessary. Otherwise, your landlord may request reliable information supporting your accommodation request.",
      },
      {
        q: "Can I submit an ESA letter obtained online?",
        a: "Yes. An ESA letter obtained through a legitimate online evaluation may support your accommodation request when issued by an appropriately qualified healthcare professional with sufficient knowledge of your circumstances. However, purchasing an online registration certificate without a meaningful evaluation does not establish eligibility.",
      },
      {
        q: "How long does a landlord have to approve an ESA request?",
        a: "There is no universal federal approval deadline for every ESA accommodation request. Housing providers should respond without undue delay. The review period may vary depending on the documentation provided and whether additional information is reasonably necessary.",
      },
      {
        q: "Can a landlord refuse an ESA because of a no-pets policy?",
        a: "A no-pets policy alone does not automatically justify denying a qualifying reasonable accommodation request in housing covered by the Fair Housing Act. However, the housing provider may consider whether the request is legally required and reasonable under the specific circumstances. Since May 2026, HUD's own enforcement has focused on individually trained animals, so for an untrained emotional support animal, state and local law and private legal options may matter more.",
      },
      {
        q: "Do I have to pay a pet deposit after ESA approval?",
        a: "For an individually trained assistance animal in covered housing, a housing provider generally should not charge an additional pet deposit or pet fee. For an untrained emotional support animal the answer is less settled: HUD's May 2026 enforcement memorandum no longer expects housing providers to waive pet fees for untrained animals as a matter of course, so state and local law and your circumstances matter. Tenants may remain responsible for property damage caused by their animals under applicable housing rules.",
      },
      {
        q: "Does an ESA letter guarantee housing approval?",
        a: "No. An ESA letter provides supporting documentation for your disability-related need for the animal. Your landlord must consider the accommodation request under applicable housing laws, but approval depends on the individual circumstances.",
      },
      {
        q: "What should I do if my landlord denies my ESA request?",
        a: "Ask for a written explanation of the denial and review whether additional supporting information is needed. If you believe your request was unlawfully denied, consider contacting a fair housing organization, your state or local fair housing agency, or an attorney. HUD still accepts housing discrimination complaints, but since May 2026 it finds reasonable cause only where an animal is individually trained to perform disability-related tasks, so private legal action and state or local protections may matter more for an untrained emotional support animal.",
      },
      {
        q: "Do I need to renew my ESA letter every year for housing?",
        a: "Federal fair housing law does not establish a universal requirement that every ESA letter must be renewed annually. However, updated information may be reasonably requested in certain circumstances, and applicable state requirements may vary.",
      },
    ],
  },

  "/esa-housing-mistakes-to-avoid": {
    route: "/esa-housing-mistakes-to-avoid",
    h1: "9 Housing Mistakes ESA Owners Should Avoid",
    metaTitle: "9 ESA Housing Mistakes to Avoid as a Tenant",
    metaDescription:
      "Avoid common ESA housing mistakes. Learn how to request an accommodation, use reliable documentation, handle pet fees, and protect your rental rights.",
    breadcrumb: "ESA Housing Mistakes to Avoid",
    heroImage: "/assets/blog/esa-housing-guides/esa-housing-mistakes-to-avoid-1200x630.webp",
    heroAlt: "Couple relaxing on a sofa at home with their tricolour dog beside them",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-24",
    dateModified: "2026-09-24",
    articleSection: "Housing Rights",
    keywords: [
      "ESA housing mistakes to avoid",
      "ESA letter for housing",
      "emotional support animal letter housing",
      "landlord emotional support animal letter",
      "emotional support animal requirements",
    ],
    icon: "ri-error-warning-line",
    faqs: [
      {
        q: "Is an ESA letter enough to guarantee a rental accommodation?",
        a: "No. An ESA letter may support a disability-related request, but a housing provider must consider the applicable law and the specific circumstances. A letter does not guarantee approval in every property.",
      },
      {
        q: "Can I request accommodation in a no-pets apartment?",
        a: "Yes, if the housing is covered by applicable fair housing protections and you have a qualifying disability-related need, you may request an exception to a no-pets policy. The provider must evaluate the request under the rules that apply.",
      },
      {
        q: "What should I do if my landlord asks for more documentation?",
        a: "Ask what information is missing. If the request is reasonably related to verifying your disability-related need, consult your healthcare professional about providing relevant clarification without disclosing unrelated medical details.",
      },
      {
        q: "Is ESA registration required for housing?",
        a: "No federal ESA registration certificate is required to establish a disability-related housing need. A commercial registration card alone is not a substitute for reliable supporting documentation when documentation is necessary.",
      },
      {
        q: "Can my landlord charge pet rent for an approved ESA?",
        a: "An approved, individually trained assistance animal in covered housing generally should not incur ordinary pet rent or a pet deposit. For an untrained emotional support animal this is less settled since HUD's May 2026 enforcement memorandum, and state and local rules may decide it. You may still be responsible for damage your animal causes under applicable tenant-damage rules.",
      },
      {
        q: "Can my ESA be denied because of its breed or size?",
        a: "A blanket pet-policy restriction should not replace an individualized assessment of a qualifying assistance-animal request. A housing provider may, however, consider evidence about the particular animal and whether a direct threat or significant damage risk can be adequately reduced.",
      },
      {
        q: "Do emotional support animal requirements include special training?",
        a: "Emotional support animal requirements for housing do not generally include training an ESA to perform disability-related tasks. That task-training distinction applies to service animals. However, since May 2026 HUD's own enforcement focuses on individually trained animals, so an untrained ESA's protection may depend more on state and local law and private legal options. You are still responsible for controlling your animal and following applicable health and safety rules.",
      },
      {
        q: "What if my landlord does not respond to my request?",
        a: "Follow up in writing, confirm that your request reached the right person, and ask whether more information is needed. For an unreasonable delay, contact a fair housing organization.",
      },
    ],
  },
};

/** Ordered list — used by the guard, the prerender entry and the related-guides section. */
export const ESA_HOUSING_GUIDE_ROUTES: string[] = Object.keys(ESA_HOUSING_GUIDES);

export function housingGuide(route: string): HousingGuide | undefined {
  return ESA_HOUSING_GUIDES[route];
}

/**
 * The full @graph for one guide: Article + BreadcrumbList + FAQPage, built from
 * the record above so the schema cannot describe copy the page does not render.
 * Returns null for any route that is not a housing guide.
 */
export function housingGuideGraph(route: string): Record<string, unknown> | null {
  const g = ESA_HOUSING_GUIDES[route];
  if (!g) return null;
  const canonical = `${BASE_URL}${g.route}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: g.h1,
        description: g.metaDescription,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        url: canonical,
        image: [`${BASE_URL}${g.heroImage}`],
        datePublished: g.datePublished,
        dateModified: g.dateModified,
        author: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        publisher: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        articleSection: g.articleSection,
        keywords: g.keywords.join(", "),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
          { "@type": "ListItem", position: 2, name: HOUSING_GUIDES_PARENT.label, item: `${BASE_URL}${HOUSING_GUIDES_PARENT.to}` },
          { "@type": "ListItem", position: 3, name: g.breadcrumb, item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: g.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}
